import { useCallback, useEffect } from 'react';
import cx from 'clsx';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';

import styles from './bulletin.module.scss';

import type { Board, BulletinAttr, useBulletin } from './use-bulletin';
import type { EmblaCarouselType } from 'embla-carousel';
import type { ZodTypeAny } from 'zod';

const DOWNLOAD_AUTOPASS_LINK_ALPHA =
  'autopassapp-alpha://open_main_page?need_login=false';
const DOWNLOAD_AUTOPASS_LINK_PROD =
  'autopassapp://open_main_page?need_login=false';

export const getActionFromBoard = ({
  isAlphaEnv,
  board,
}: {
  isAlphaEnv: boolean;
  board: Board<unknown>;
}) => {
  const uri = (() => {
    switch (board.action.type) {
      case 'open_app':
        return isAlphaEnv
          ? DOWNLOAD_AUTOPASS_LINK_ALPHA
          : DOWNLOAD_AUTOPASS_LINK_PROD;

      case 'open_link':
        return board.action?.open_link?.uri;

      default:
        return '';
    }
  })();

  return () => {
    window.open(uri, '_blank', 'noopener noreferrer');
  };
};

const BulletinItem = ({
  title,
  subTitle,
  imageUrl,
  color = '#000000',
  backgroundColor,
  onClick,
  className,
}: PromotionItemProps) => {
  return (
    <div className={styles['itemWrapper']}>
      <div>
        <div
          className={cx(styles['item'], className)}
          onClick={onClick}
          style={{
            backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
            backgroundColor,
            color,
          }}
        >
          {title && <div>{title}</div>}
          {subTitle && <div>{subTitle}</div>}
        </div>
      </div>
    </div>
  );
};

interface PromotionItemProps {
  imageUrl: string;
  backgroundColor?: string;
  color?: string;
  title?: string;
  subTitle?: string;
  uri?: string | undefined;
  onClick: () => void;
  className?: string;
}

type BulletinProps<TAttr> = {
  handleBoardClick?: (board: Board<TAttr>) => void;
  handleBoardInView?: (board: Board<TAttr>) => void;
} & Pick<
  ReturnType<typeof useBulletin<string, ZodTypeAny, TAttr>>,
  'data' | 'isLoading' | 'isAlphaEnv'
>;

export function Bulletin<TAttr = BulletinAttr>({
  data = [],
  isLoading,
  isAlphaEnv,
  handleBoardClick,
  handleBoardInView,
}: BulletinProps<TAttr>) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [Autoplay()]);

  type ImageBoard = Board<TAttr & { image_url: string }>;

  const availData = data
    ? data.filter((d: Board<TAttr>): d is ImageBoard => {
        return !!d.attributes?.image_url;
      })
    : [];

  const logEmblaBoardEvent = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const index = emblaApi.selectedScrollSnap();
      // derived value from data, just add data as deps
      const targetBoard = availData?.[index];
      targetBoard && handleBoardInView?.(targetBoard);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, handleBoardInView],
  );

  useEffect(() => {
    if (emblaApi) emblaApi.on('slidesInView', logEmblaBoardEvent);
  }, [emblaApi, logEmblaBoardEvent]);

  if (availData.length < 1 || isLoading) {
    return (
      <div className={styles['wrapper']}>
        <div className={cx(styles['bulletin'], styles['placeholder'])}></div>
      </div>
    );
  }

  return (
    <div className={styles['wrapper']}>
      <div className={styles['viewport']} ref={emblaRef}>
        <div className={styles['container']}>
          {availData?.map((datum) => {
            const onClick = () => {
              const action = getActionFromBoard({ board: datum, isAlphaEnv });
              action();
              handleBoardClick?.(datum);
            };

            return (
              <BulletinItem
                key={datum.id}
                title={datum.attributes.title}
                subTitle={datum.attributes.sub_title}
                backgroundColor={datum.attributes.background_color}
                imageUrl={datum.attributes.image_url}
                onClick={onClick}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
